import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ago, ReadOnlyNote } from "@/pages/Community";
import { Avatar } from "@/components/Avatar";
import { RoleBadge } from "@/components/RoleBadge";

const Author = ({ a, at }) => (
  <div className="flex items-center gap-2 text-xs text-stone-500">
    <Avatar user={a} className="h-7 w-7" />
    {a.role === "tech" ? <Link to={`/tech/${a.username}`} className="font-mono text-stone-800 hover:text-pink-600">@{a.username}</Link> : <span className="font-mono">@{a.username}</span>}
    <RoleBadge role={a.role} /> · {ago(at)}
  </div>
);

export default function ThreadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [body, setBody] = useState("");
  const { data: t, refetch, isError } = useQuery({ queryKey: ["thread", id], queryFn: () => api.get(`/threads/${id}`).then((r) => r.data) });
  const isTech = user?.role === "tech";

  const reply = async (e) => {
    e.preventDefault();
    try { await api.post(`/threads/${id}/replies`, { body }); setBody(""); toast.success("Reply posted"); refetch(); } catch (err) { toast.error(errMsg(err)); }
  };
  const upvote = async () => {
    try { await api.post(`/threads/${id}/upvote`); refetch(); } catch (err) { toast.error(errMsg(err)); }
  };

  if (isError) return <p data-testid="thread-not-found" className="py-32 text-center text-stone-500">Post not found.</p>;
  if (!t) return <div className="min-h-[60vh]" />;
  return (
    <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
      <Link to="/community" data-testid="thread-back" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-pink-600"><ArrowLeft className="h-4 w-4" />Board</Link>
      <article className="rise mt-6 rounded-[2rem] border border-[#EAE4DC] bg-white p-8">
        <span className="font-mono text-[10px] uppercase tracking-widest text-pink-600">{t.category}</span>
        <h1 data-testid="thread-title" className="mt-2 font-serif text-3xl sm:text-4xl">{t.title}</h1>
        <div className="mt-4"><Author a={t.author} at={t.created_at} /></div>
        <p data-testid="thread-body" className="mt-6 whitespace-pre-line leading-relaxed text-stone-700">{t.body}</p>
        <button data-testid="thread-upvote-button" onClick={upvote} disabled={!isTech}
          className={`mt-6 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${t.upvoted ? "border-pink-500 bg-pink-50 text-pink-700" : "hover:border-stone-900"}`}>
          <ArrowUp className="h-4 w-4" /><span data-testid="thread-upvote-count">{t.upvotes}</span> helpful
        </button>
      </article>
      <h2 className="mt-12 font-mono text-xs uppercase tracking-[0.2em] text-stone-500">{t.replies.length} replies</h2>
      <div className="mt-4 space-y-4">
        {t.replies.map((r) => (
          <div key={r.id} data-testid="thread-reply" className="rounded-2xl border border-[#EAE4DC] bg-white p-6">
            <Author a={r.author} at={r.created_at} />
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-stone-700">{r.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-8">
        {isTech ? (
          <form onSubmit={reply} className="space-y-3">
            <Textarea data-testid="reply-input" required rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share what's worked for you…" className="rounded-xl bg-white" />
            <button data-testid="reply-submit" className="rounded-full bg-stone-900 px-6 py-3 text-sm text-white hover:bg-pink-600">Reply</button>
          </form>
        ) : <ReadOnlyNote user={user} />}
      </div>
    </div>
  );
}
